import { IS_JP_ONLY_BUILD, JP_ONLY_FONT_FAMILY } from '@/common/const/build-locale'

const DEFAULT_ECHARTS_FONT_FAMILY =
  '"Helvetica Neue", Helvetica, Arial, "PingFang SC", "Hiragino Sans GB", "Heiti SC", "Microsoft YaHei", "WenQuanYi Micro Hei", sans-serif'

/** ECharts 画布文本字体（Canvas 不继承页面 CSS，需显式配置） */
export function getEchartsFontFamily (): string {
  return IS_JP_ONLY_BUILD ? JP_ONLY_FONT_FAMILY : DEFAULT_ECHARTS_FONT_FAMILY
}

export function withEchartsTextStyle (textStyle: Record<string, unknown> = {}) {
  return {
    fontFamily: getEchartsFontFamily(),
    ...textStyle,
  }
}

function patchTextStyleObject (
  textStyle: Record<string, unknown> | undefined,
  fontFamily: string,
) {
  if (!textStyle || typeof textStyle !== 'object') return
  if (!textStyle.fontFamily) textStyle.fontFamily = fontFamily
}

function patchLabelLike (label: unknown, fontFamily: string) {
  if (!label || typeof label !== 'object') return
  const labelObj = label as Record<string, unknown>
  if (!labelObj.fontFamily) labelObj.fontFamily = fontFamily
}

function patchAxis (axis: unknown, fontFamily: string) {
  if (!axis) return
  const axes = Array.isArray(axis) ? axis : [axis]
  axes.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const axisItem = item as Record<string, unknown>
    const axisLabel = axisItem.axisLabel as Record<string, unknown> | undefined
    if (axisLabel) {
      if (axisLabel.textStyle && typeof axisLabel.textStyle === 'object') {
        patchTextStyleObject(axisLabel.textStyle as Record<string, unknown>, fontFamily)
      } else if (!axisLabel.fontFamily) {
        axisLabel.fontFamily = fontFamily
      }
    }
    const nameTextStyle = axisItem.nameTextStyle as Record<string, unknown> | undefined
    patchTextStyleObject(nameTextStyle, fontFamily)
  })
}

function patchSeriesData (data: unknown, fontFamily: string) {
  if (!Array.isArray(data)) return
  data.forEach((point) => {
    if (!point || typeof point !== 'object') return
    const pointItem = point as Record<string, unknown>
    patchLabelLike(pointItem.label, fontFamily)
  })
}

function patchSeries (series: unknown, fontFamily: string) {
  if (!series) return
  const list = Array.isArray(series) ? series : [series]
  list.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const seriesItem = item as Record<string, unknown>
    patchLabelLike(seriesItem.label, fontFamily)
    const emphasis = seriesItem.emphasis as Record<string, unknown> | undefined
    if (emphasis) patchLabelLike(emphasis.label, fontFamily)
    const labelLine = seriesItem.labelLine as Record<string, unknown> | undefined
    if (labelLine && !labelLine.fontFamily) labelLine.fontFamily = fontFamily
    const data = seriesItem.data
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      patchLabelLike((data as Record<string, unknown>).label, fontFamily)
    } else {
      patchSeriesData(data, fontFamily)
    }
  })
}

/** 为图表 option 注入统一字体（饼图/柱图/折线图等） */
export function applyEchartsFontToOption<T extends Record<string, unknown>> (option: T): T {
  const fontFamily = getEchartsFontFamily()
  const root = option as Record<string, unknown>

  const rootTextStyle = (root.textStyle as Record<string, unknown> | undefined) || {}
  root.textStyle = { fontFamily, ...rootTextStyle }

  const title = root.title
  if (title && typeof title === 'object') {
    const titles = Array.isArray(title) ? title : [title]
    titles.forEach((item) => {
      if (!item || typeof item !== 'object') return
      const titleItem = item as Record<string, unknown>
      const textStyle = (titleItem.textStyle as Record<string, unknown> | undefined) || {}
      titleItem.textStyle = { fontFamily, ...textStyle }
    })
  }

  const legend = root.legend
  if (legend && typeof legend === 'object') {
    const legends = Array.isArray(legend) ? legend : [legend]
    legends.forEach((item) => {
      if (!item || typeof item !== 'object') return
      const legendItem = item as Record<string, unknown>
      const textStyle = (legendItem.textStyle as Record<string, unknown> | undefined) || {}
      legendItem.textStyle = { fontFamily, ...textStyle }
    })
  }

  const tooltip = root.tooltip
  if (tooltip && typeof tooltip === 'object') {
    const tooltips = Array.isArray(tooltip) ? tooltip : [tooltip]
    tooltips.forEach((item) => {
      if (!item || typeof item !== 'object') return
      const tooltipItem = item as Record<string, unknown>
      const textStyle = (tooltipItem.textStyle as Record<string, unknown> | undefined) || {}
      tooltipItem.textStyle = { fontFamily, ...textStyle }
    })
  }

  patchAxis(root.xAxis, fontFamily)
  patchAxis(root.yAxis, fontFamily)
  patchSeries(root.series, fontFamily)

  return option
}

/** 自定义 HTML tooltip 内联样式用（appendToBody 的 DOM 不继承页面字体） */
export function echartsTooltipFontFamilyCss (): string {
  return `font-family: ${getEchartsFontFamily()};`
}
